import EventBus from './event-bus'
import notify from '../utils/mixin-notify'
import { GET_DATA_FIELD_PROPS } from '../consts'
import { cloneDeep, get } from 'lodash'
import { PANEL_TYPES } from '../components/layout-manager/gui-editor/options/schema'

export default {
  mixins: [notify],
  data() {
    return {
      isEditableMode: false,
      fieldPropertiesMap: {},
      loading: false,
      editableRowIndex: null,
      isNewRow: false,
    }
  },
  computed: {
    listType() {
      return this.$get('listType')
    },
    isEditableList() {
      return this.listType === PANEL_TYPES.EDITABLE_LIST
    },
    panelUid() {
      return this.$get('panelUid')
    },
    showAddRowButton() {
      return this.$get('showAddRowButton') && this.isEditableList
    },
    fetchParameters() {
      return this.$get('currentParameters') || {}
    },
    id() {
      return this.$get('id')
    },
  },
  methods: {
    isEditableRow(row) {
      const rowId = get(row, 'ROWNUMID')
      return rowId === this.editableRowIndex && this.isEditableMode
    },
    isDisabledRow(row) {
      const rowId = get(row, 'ROWNUMID')
      return rowId !== this.editableRowIndex && this.isEditableMode
    },
    async getFormProperties() {
      const response = await this.$client.execute({
        operationId: 'Dynform_getProperties',
        method: 'get',
        parameters: {
          idForm: this.mongoId,
        },
      })

      return get(response, 'body', {})
    },
    async setFormProperties() {
      const properties = await this.getFormProperties()

      const expressionChain = get(properties, 'expressionChain', {})
      const fieldPropertiesMap = get(properties, 'fieldPropertiesMap', {})
      const fieldPathMap = get(properties, 'fieldPathMap', {})

      this.$dispatch('setFieldPathMap', { fieldPathMap })
      this.$dispatch('setExpressionChain', { expressionChain })
      this.$dispatch('setFieldPropertiesMap', { fieldPropertiesMap })
    },
    async handleRowClick(rowData) {
      if (!this.isEditableList) return
      if (this.isEditableMode) return
      if (this.isLockedRow(rowData)) return this.showLockedNotification()
      this.setEditableMode(true)
      this.setTableLoading(true)

      const idMaintable = get(rowData, 'ROW_KEY')
      await this.setFieldProperties(idMaintable)

      const index = this.extendedListData.findIndex((data) => data === rowData)
      this.setEditableRowIndex(index)

      this.setTableLoading(false)
    },
    hideErrors() {
      const alert = document.querySelector('.verification-alert')
      if (alert) alert.parentNode.removeChild(alert)
    },
    showErrors(row, errors) {
      const panelElement = document.querySelector(`div[paneluid="${this.panelUid}"]`)
      if (panelElement) {
        const tableElement = panelElement.querySelector('.el-table__body')
        if (tableElement) {
          const indexToInsertAfter = row.ROWNUMID
          const newRow = tableElement.insertRow(indexToInsertAfter + 1)
          const tableWidth = tableElement.offsetWidth

          let errorMessages = ''
          for (const { LABEL, PUBLIC } of errors) {
            errorMessages += `<p class="el-alert__description"><span class="fw-bold">${this.$i18n.t(
              LABEL
            )}</span> - ${this.$i18n.t(PUBLIC)}</p>`
          }

          const alert = document.createElement('div')
          alert.className = 'el-alert el-alert--error is-light verification-alert'
          alert.role = 'alert'
          alert.style.width = `${tableWidth}px`
          alert.innerHTML = `<i class="el-alert__icon el-icon-error is-big"></i>
             <div class="el-alert__content">
              <span class="el-alert__title is-bold">${this.$i18n.t('save_data_error_title')}</span>
              ${errorMessages}
              <i class="el-alert__closebtn el-icon-close"></i>
            </div>`

          newRow.appendChild(alert)

          this.$nextTick(() => {
            const closeButton = document.querySelector('.verification-alert .el-alert__closebtn')
            if (!closeButton) return
            closeButton.addEventListener('click', () => {
              this.hideErrors()
            })
          })
        }
      }
    },
    async showSuccessNotification() {
      return this.showNotification({
        title: 'form_save_success',
        text: 'form_save_success',
        type: 'success',
      })
    },
    async showErrorNotification() {
      return this.showNotification({
        title: 'form_save_error',
        text: 'form_save_error',
        type: 'error',
      })
    },
    async showAlerts(alerts = []) {
      for (const alert of alerts) {
        await this.showNotification({
          title: 'form_save_alert',
          text: alert.PUBLIC,
          type: 'info',
        })
      }
    },
    async showNotices(notices = []) {
      for (const notice of notices) {
        await this.showNotification({
          title: 'form_save_notice',
          text: notice.PUBLIC,
          type: 'info',
        })
      }
    },
    async showWarnings(warnings = []) {
      for (const warning of warnings) {
        await this.showNotification({
          title: 'form_save_warning',
          text: warning.PUBLIC,
          type: 'warning',
        })
      }
    },

    async handleSaveDataResponse(header = {}, row) {
      const { ALERT: alerts, ERROR: errors, NOTICE: notices, WARNING: warnings } = header

      if (this.hasError(errors)) return this.showErrors(row, errors)

      await this.showSuccessNotification()
      this.showAlerts(alerts)
      this.showNotices(notices)
      this.showWarnings(warnings)
    },
    setTableLoading(isLoading) {
      this.loading = isLoading
    },
    getPreparedFormData() {
      const fieldPropertiesMap = this.$get('fieldPropertiesMap')
      const formData = {}
      for (const { prop: name } of this.tableData) {
        const field = fieldPropertiesMap[name]
        if (field) {
          formData[field.id] = field.value
        }
      }

      return formData
    },
    closeEditMode() {
      EventBus.$emit(`refresh-list-data-${this.id}`, this.id)
      this.setEditableRowIndex(null)
      this.setEditableMode(false, false)
    },
    hasError(errors) {
      return !!errors.length
    },
    async saveData(formData, row) {
      const parameters = {
        idForm: this.mongoId,
        idMaintable: get(row, 'ROW_KEY'),
        formData: JSON.stringify(formData),
      }

      await this.$client
        .execute({
          operationId: 'Dynform_saveData',
          method: 'post',
          parameters,
          requestContentType: 'application/x-www-form-urlencoded',
        })
        .then((response) => response.body)
        .then((response) => {
          this.hideErrors()
          this.handleSaveDataResponse(response.HEADER, row)
          this.setTableLoading(false)
          const errors = get(response, 'HEADER.ERROR', [])
          if (!this.hasError(errors)) this.closeEditMode()
        })
        .catch(() => this.showErrorNotification())
    },
    async saveRow(row) {
      this.setTableLoading(true)
      const formData = this.getPreparedFormData()
      await this.saveData(formData, row)
    },
    addNewRowToListData() {
      const listData = cloneDeep(this.listData)
      listData.push({ ROWNUMID: listData.length })
      this.$dispatch('setListData', { listData })
    },
    removeNewRowFromListData() {
      const listData = cloneDeep(this.listData)
      listData.pop()
      this.$dispatch('setListData', { listData })
    },
    async getData(idMaintable) {
      const parameters = {
        idForm: this.mongoId,
        idMaintable,
      }
      const data = await this.$client
        .execute({
          operationId: 'Dynform_getData',
          parameters,
        })
        .catch(() =>
          this.showNotification({
            title: 'dynform_get_data_error_title',
            text: 'dynform_get_data_error_message',
          })
        )

      return get(data, 'body.ITEMS', {})
    },
    getValue(field, data) {
      const value = get(data, [field.id, GET_DATA_FIELD_PROPS.VALUE])
      return field.type === 'number' ? Number(value) : value
    },
    async setFieldProperties(idMaintable) {
      const data = await this.getData(idMaintable)
      const clonedFieldProperties = cloneDeep(this.$get('fieldPropertiesMap'))
      for (const { prop } of this.tableData) {
        const field = clonedFieldProperties[prop]

        if (!field) continue

        field.value = get(data, [field.id, GET_DATA_FIELD_PROPS.VALUE])
        field.readOnly = get(data, [field.id, GET_DATA_FIELD_PROPS.READ_ONLY], field.readOnly)
        field.hidden = get(data, [field.id, GET_DATA_FIELD_PROPS.HIDDEN], field.hidden)
        field.list = get(data, [field.id, GET_DATA_FIELD_PROPS.LIST], field.list)
        field.autocomplete = get(data, [field.id, GET_DATA_FIELD_PROPS.AUTOCOMPLETE], field.autocomplete)
        field.label = get(data, [field.id, GET_DATA_FIELD_PROPS.LABEL], field.label)
        field.required = get(data, [field.id, GET_DATA_FIELD_PROPS.REQUIRED], field.required)
        field.formattedValue = get(data, [field.id, GET_DATA_FIELD_PROPS.FORMATTED_VALUE], field.formattedValue)
      }

      this.$dispatch('setFieldPropertiesMap', { fieldPropertiesMap: clonedFieldProperties })
    },
    setEditableMode(isEditableMode, isNewRow = false) {
      this.isEditableMode = isEditableMode
      this.isNewRow = isNewRow
    },
    setEditableRowIndex(index) {
      this.editableRowIndex = index
    },
    async addRow() {
      this.setTableLoading(true)
      this.addNewRowToListData()
      this.setEditableRowIndex(this.listData.length - 1)
      await this.setFieldProperties()
      this.setEditableMode(true, true)
      this.$nextTick(() => this.scrollDetailIntoView(this.editableRowIndex))
      this.setTableLoading(false)
    },
    async getConfirmation() {
      return await this.$confirm(this.$i18n.t('discard_edit_confirmation_message'))
    },
    async discardEdit() {
      if (!(await this.getConfirmation())) return

      this.setEditableRowIndex(null)
      if (this.isNewRow) this.removeNewRowFromListData()
      this.setEditableMode(false, false)
      this.hideErrors()
    },
    showLockedNotification() {
      this.showNotification({
        title: 'row_is_locked_title',
        text: 'row_is_locked_message',
        type: 'warning',
      })
    },
    isLockedRow(row) {
      return get(row, 'RECORD_LOCK.isLocked', false)
    },
  },
}
